<template>
  <div class="modal fade" id="modalReservationDetail" ref="modal" data-bs-backdrop="static" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content border-0 shadow-lg" style="border-radius: 16px;">

        <div
          class="modal-header pe-4 text-white"
          :class="'bg-' + headerColor"
          style="border-radius: 16px 16px 0 0;"
        >
          <div>
            <h4 class="modal-title fw-bold mb-0">
              <i :class="stateIcon" class="me-2"/>
              {{ $t(stateLabel) }}
            </h4>
            <div class="small opacity-75 mt-1">
              <i class="fa-regular fa-clock me-1"/>{{ formatDateTime(reservation.reserve_at) }}
            </div>
          </div>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"/>
        </div>

        <div class="modal-body px-4 py-4">
          <!-- Time block -->
          <div class="rsv-time-block mb-3">
            <div class="rsv-big-time">
              <i class="fa-regular fa-clock me-2"/>{{ formatTime(reservation.reserve_at) }}
            </div>
            <div class="text-muted small">{{ formatDate(reservation.reserve_at) }}</div>
          </div>

          <!-- Info card -->
          <div class="card border-0 shadow-sm rounded-3 mb-3">
            <div class="card-body">
              <div class="row g-3">
                <div class="col-12">
                  <div class="text-muted small fw-bold mb-1">
                    <i class="fa-solid fa-user me-1"/>{{ $t('Customer Name') }}
                  </div>
                  <div class="fw-bold text-dark">{{ reservation.name || '-' }}</div>
                </div>

                <div class="col-6">
                  <div class="text-muted small fw-bold mb-1">
                    <i class="fa-solid fa-phone me-1"/>{{ $t('Telephone') }}
                  </div>
                  <div class="fw-bold text-dark">
                    <a v-if="reservation.telephone" :href="'tel:' + reservation.telephone" class="text-decoration-none text-dark">
                      {{ reservation.telephone }}
                    </a>
                    <span v-else>-</span>
                  </div>
                </div>

                <div class="col-6">
                  <div class="text-muted small fw-bold mb-1">
                    <i class="fa-solid fa-users me-1"/>{{ $t('Guests') }} (Pax)
                  </div>
                  <div class="fw-bold text-dark">{{ reservation.pax }}</div>
                </div>

                <div class="col-12">
                  <div class="text-muted small fw-bold mb-1">
                    <i class="fa-solid fa-chair me-1"/>{{ $t('Table') }}
                  </div>
                  <div v-if="assignedTable" class="fw-bold text-dark">
                    {{ assignedTable.name }}
                    <small class="text-muted ms-1">/ {{ $t(assignedTable.zone) }}</small>
                    <span class="text-muted small ms-2">
                      <i class="fa-solid fa-user-group me-1"/>{{ assignedTable.seat_capacity }}
                    </span>
                  </div>
                  <div v-else class="fw-bold text-danger">
                    <i class="fa-solid fa-circle-exclamation me-1"/>{{ $t('No Table Assigned') }}
                  </div>
                </div>

                <div class="col-12" v-if="reservation.note">
                  <div class="text-muted small fw-bold mb-1">
                    <i class="fa-solid fa-note-sticky me-1"/>{{ $t('Note') }}
                  </div>
                  <div class="rsv-note">{{ reservation.note }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Inline assign-table (only for pending without a table) -->
          <div v-if="needsTableAssignment" class="mb-3">
            <div class="alert alert-warning border-0 rounded-3 d-flex align-items-center justify-content-between mb-2 py-2">
              <div class="small fw-bold">
                <i class="fa-solid fa-circle-exclamation me-1"/>{{ $t('Please assign a table to open it') }}
              </div>
              <button
                v-if="!showTablePicker"
                type="button"
                class="btn btn-sm btn-warning fw-bold"
                @click="showTablePicker = true"
              >
                <i class="fa-solid fa-chair me-1"/>{{ $t('Assign Table') }}
              </button>
            </div>

            <div v-if="showTablePicker" class="card border-0 shadow-sm rounded-3">
              <div class="card-body p-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <div class="fw-bold small">{{ $t('Select an available table') }}</div>
                  <button type="button" class="btn btn-sm btn-link p-0" @click="showTablePicker = false">
                    <i class="fa-solid fa-xmark me-1"/>{{ $t('Close') }}
                  </button>
                </div>
                <div class="table-pick-grid">
                  <button
                    type="button"
                    v-for="t in availableTables"
                    :key="t.id"
                    class="table-pick"
                    :class="{ 'undersize': t.seat_capacity < reservation.pax }"
                    @click="assignTable(t.id)"
                  >
                    <div class="fw-bold">{{ t.name }}</div>
                    <div class="small text-muted">{{ $t(t.zone) }}</div>
                    <div class="small">
                      <i class="fa-solid fa-user-group me-1"/>{{ t.seat_capacity }}
                    </div>
                  </button>
                </div>
                <div v-if="availableTables.length === 0" class="text-muted text-center small py-3">
                  {{ $t('No available tables') }}
                </div>
              </div>
            </div>
          </div>

          <!-- Primary actions (pending only) -->
          <div v-if="reservation.state === 'pending'" class="row g-2 mb-3">
            <div class="col-12">
              <button
                type="button"
                class="btn btn-theme w-100 py-3 fw-bold fs-5 text-white"
                style="border-radius: 12px; box-shadow: 0 4px 10px rgba(234, 100, 6, 0.3);"
                :disabled="!reservation.table_id"
                @click="$emit('open-table', reservation)"
              >
                <i class="fa-solid fa-check-circle me-2"/>{{ $t('Open Table') }}
              </button>
            </div>
            <div class="col-12">
              <button
                type="button"
                class="btn btn-outline-danger w-100 py-2 fw-bold rounded-3"
                @click="$emit('cancel-reservation', reservation)"
              >
                <i class="fa-solid fa-ban me-1"/>{{ $t('Cancel Reservation') }}
              </button>
            </div>
          </div>

          <!-- Arrived state info -->
          <div v-else-if="reservation.state === 'arrived'" class="alert alert-success border-0 rounded-3 mb-3 py-2 small">
            <i class="fa-solid fa-utensils me-1"/>{{ $t('Customer arrived — table opened') }}
          </div>

          <!-- Cancelled state info -->
          <div v-else-if="reservation.state === 'cancelled'" class="alert alert-danger border-0 rounded-3 mb-3 py-2 small">
            <i class="fa-solid fa-ban me-1"/>{{ $t('This reservation was cancelled') }}
          </div>

          <!-- Footer actions -->
          <div class="d-flex gap-2 pt-3 border-top">
            <button
              v-if="reservation.state !== 'arrived'"
              type="button"
              class="btn btn-outline-primary fw-bold rounded-3 flex-fill"
              @click="$emit('edit', reservation)"
            >
              <i class="fa-solid fa-square-pen me-1"/>{{ $t('Edit') }}
            </button>
            <button
              type="button"
              class="btn btn-outline-danger fw-bold rounded-3 flex-fill"
              @click="$emit('delete', reservation.id)"
            >
              <i class="fa-solid fa-trash-can me-1"/>{{ $t('Delete') }}
            </button>
            <button
              type="button"
              class="btn btn-light fw-bold rounded-3 flex-fill border"
              data-bs-dismiss="modal"
            >
              {{ $t('Close') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

const STATE_META = {
  pending:   { color: 'warning', label: 'Pending',   icon: 'fa-solid fa-hourglass-half' },
  arrived:   { color: 'success', label: 'Arrived',   icon: 'fa-solid fa-utensils'      },
  cancelled: { color: 'danger',  label: 'Cancelled', icon: 'fa-solid fa-ban'           }
};

export default {
  name: 'ModalReservationDetail',
  props: {
    reservation: { type: Object, required: true },
    tables: { type: Array, default: () => [] }
  },
  emits: ['close', 'edit', 'open-table', 'cancel-reservation', 'assign-table', 'delete'],
  data() {
    return {
      showTablePicker: false
    };
  },
  computed: {
    headerColor() {
      return (STATE_META[this.reservation.state] || {}).color || 'secondary';
    },
    stateIcon() {
      return (STATE_META[this.reservation.state] || {}).icon || 'fa-solid fa-circle';
    },
    stateLabel() {
      return (STATE_META[this.reservation.state] || {}).label || this.reservation.state;
    },
    assignedTable() {
      if (!this.reservation.table_id) return null;
      return this.tables.find(t => t.id === this.reservation.table_id) || null;
    },
    needsTableAssignment() {
      return this.reservation.state === 'pending' && !this.reservation.table_id;
    },
    availableTables() {
      // Sort by fits-pax then by capacity.
      const pax = this.reservation.pax || 0;
      return [...this.tables]
        .filter(t => t.state === 'available')
        .sort((a, b) => {
          const aFits = a.seat_capacity >= pax ? 0 : 1;
          const bFits = b.seat_capacity >= pax ? 0 : 1;
          if (aFits !== bFits) return aFits - bFits;
          return a.seat_capacity - b.seat_capacity;
        });
    }
  },
  methods: {
    formatDate(iso) { return moment(iso).format('ddd, DD MMM YYYY'); },
    formatTime(iso) { return moment(iso).format('HH:mm'); },
    formatDateTime(iso) { return moment(iso).format('DD MMM YYYY HH:mm'); },
    assignTable(table_id) {
      this.$emit('assign-table', { id: this.reservation.id, table_id });
      this.showTablePicker = false;
    }
  },
  mounted() {
    $(this.$refs.modal).modal('show');
    $(this.$refs.modal).on('hidden.bs.modal', () => {
      this.$emit('close');
    });
  }
};
</script>

<style scoped>
.rsv-time-block { text-align: center; }
.rsv-big-time {
  font-size: 2rem;
  font-weight: 800;
  color: #212529;
  line-height: 1.1;
}
.rsv-note {
  background: #fff8e6;
  border-left: 3px solid #ffc107;
  padding: 8px 12px;
  border-radius: 6px;
  color: #495057;
  white-space: pre-line;
}

.table-pick-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 8px;
}

.table-pick {
  background: #fff;
  border: 2px solid #dee2e6;
  border-radius: 10px;
  padding: 8px;
  text-align: center;
  transition: all 0.15s ease;
  cursor: pointer;
}

.table-pick:hover {
  border-color: #fd7e14;
  background: #fff5ec;
}

.table-pick.undersize {
  opacity: 0.6;
}
</style>
